import isArray from 'lodash/isArray'
import moment from 'moment'

/**
 * Pure helpers for the Pre-Bookings admin pages.
 *
 * Kept free of React so the funnel arithmetic and the row derivations can be
 * unit-tested directly (see preBookingHelpers.test.js), mirroring
 * pages/booking/bookingDetailHelpers.js.
 */

/**
 * Intent pickup/return are wall-clock (stored as UTC-naive). Format UTC digits
 * only — never shift into the admin browser timezone.
 */
export const formatWallClockDateTime = (value, pattern = 'DD MMM YYYY · HH:mm') => {
  if (!value) return '-'
  const m = moment.utc(value)
  return m.isValid() ? m.format(pattern) : '-'
}

export const formatWallClockDate = value => formatWallClockDateTime(value, 'DD MMM')

/**
 * The list endpoint returns `{ total, pageSize }` while `Table`'s infinite
 * scroll needs a page count. Derived here rather than adding a third pagination
 * shape to the API, which already has two.
 */
export const totalPagesFrom = (total, pageSize) => {
  const size = Number(pageSize)
  const count = Number(total)
  if (!Number.isFinite(size) || size <= 0) return 1
  if (!Number.isFinite(count) || count <= 0) return 1
  return Math.ceil(count / size)
}

/** True while there is another page of rows to append. */
export const hasMorePages = pagination => {
  const { total, pageSize, pageNumber } = pagination || {}
  return Number(pageNumber || 1) < totalPagesFrom(total, pageSize)
}

const percentage = (part, whole) => {
  const numerator = Number(part)
  const denominator = Number(whole)
  if (!Number.isFinite(denominator) || denominator <= 0) return 0
  if (!Number.isFinite(numerator) || numerator <= 0) return 0
  return Number(((numerator / denominator) * 100).toFixed(2))
}

/**
 * The API returns `conversionRate` but no abandonment figure, so it is derived
 * from two counts that are already in the same payload.
 */
export const abandonmentRate = funnel => percentage(funnel?.abandoned, funnel?.carViews)

/** Prefer the server's figure; fall back to the same arithmetic if absent. */
export const conversionRate = funnel => (Number.isFinite(Number(funnel?.conversionRate)) ? Number(funnel.conversionRate) : percentage(funnel?.confirmed, funnel?.carViews))

/**
 * Funnel stages in order. "Car views" are intents created — an intent is created
 * on every car view, so there is no separate view counter to reconcile against.
 */
export const toFunnelStages = (funnel, labels = {}) => [
  { key: 'carViews', label: labels.carViews || 'Car views', count: Number(funnel?.carViews || 0) },
  { key: 'datesSelected', label: labels.datesSelected || 'Dates selected', count: Number(funnel?.datesSelected || 0) },
  { key: 'readyForConfirmation', label: labels.readyForConfirmation || 'Ready', count: Number(funnel?.readyForConfirmation || 0) },
  { key: 'confirmed', label: labels.confirmed || 'Confirmed', count: Number(funnel?.confirmed || 0) },
  { key: 'abandoned', label: labels.abandoned || 'Abandoned', count: Number(funnel?.abandoned || 0) },
]

/** Guest intents carry a sessionId and no userId; the model enforces exactly one. */
export const isGuestIntent = intent => !intent?.userId

/** Display name for whoever started the intent. */
export const ownerLabel = (intent, guestText = 'Guest') => {
  if (isGuestIntent(intent)) return guestText
  return intent?.userId?.fullName || intent?.userId?.email || intent?.userId?.phone || '-'
}

/**
 * Flattens the reminder audit trail into one line per stage, e.g.
 * `1h · push, email`. Records attempts per channel — there is no delivery
 * receipt, so this must not be presented as proof a reminder arrived.
 */
export const summariseReminders = remindersSent => {
  if (!isArray(remindersSent) || remindersSent.length === 0) return []
  return remindersSent.map(reminder => ({
    stage: reminder?.stage || '-',
    channels: isArray(reminder?.channels) ? reminder.channels : [],
    sentAt: reminder?.sentAt || null,
    label: `${reminder?.stage || '-'} · ${(reminder?.channels || []).join(', ') || '-'}`,
  }))
}

/** Query params for the list endpoint, omitting anything unset. */
export const buildListParams = (filter, page, pageSize = 25) => {
  const params = { pageNumber: page, pageSize }
  if (filter?.status) params.status = filter.status
  if (filter?.user) params.user = filter.user
  if (filter?.car) params.car = filter.car
  if (filter?.from) params.from = filter.from
  if (filter?.to) params.to = filter.to
  if (filter?.guest) params.guest = filter.guest
  if (filter?.reminded) params.reminded = filter.reminded
  return params
}
